import { createContext, useContext, useState, type ReactNode } from 'react';
import { useStore, type StoreApi } from 'zustand';

import type { AskConversationController } from '@/hooks/ask/useAskConversationController';
import { createAskUiStore, type AskUiStore } from '@/services/stores/askUiStore';

const AskUiContext = createContext<StoreApi<AskUiStore> | null>(null);

/**
 * The open conversation's controller. Carried by context because the compact
 * layout's sheets portal outside the screen tree and must re-provide it.
 */
const AskConversationContext = createContext<AskConversationController | null>(null);

export function AskConversationProvider({
  value,
  children,
}: {
  value: AskConversationController;
  children: ReactNode;
}) {
  return (
    <AskConversationContext.Provider value={value}>{children}</AskConversationContext.Provider>
  );
}

export function useAskConversationContext(): AskConversationController {
  const value = useContext(AskConversationContext);

  if (!value) {
    throw new Error('useAskConversationContext must be used inside AskConversationProvider');
  }

  return value;
}

/**
 * Creates the Ask UI store for as long as this provider is mounted. The Ask
 * layout mounts it once for both routes; a compact-layout sheet re-provides the
 * same store inside its portal with `value`.
 */
export function AskUiProvider({
  value,
  children,
}: {
  value?: StoreApi<AskUiStore>;
  children: ReactNode;
}) {
  const [ownStore] = useState(createAskUiStore);

  return <AskUiContext.Provider value={value ?? ownStore}>{children}</AskUiContext.Provider>;
}

export function useAskUiStoreApi(): StoreApi<AskUiStore> {
  const store = useContext(AskUiContext);

  if (!store) {
    throw new Error('useAskUiStoreApi must be used inside AskUiProvider');
  }

  return store;
}

/** Subscribes to one slice of the Ask UI store. Always pass a narrow selector. */
export function useAskUiStore<Selected>(selector: (state: AskUiStore) => Selected): Selected {
  return useStore(useAskUiStoreApi(), selector);
}
